"use strict";
/**
 * Workflow layout — where the cards, frames, edges and insert places of a
 * workflow sit, for a flow that runs left to right or top to bottom.
 *
 * Pure: the same input gives the same output. Stored step positions are not
 * read. The layout works on two axes — "along" the flow and "across" it — and
 * turns them into x and y only at the end.
 *
 * Cards never overlap. Every step hangs below one predecessor. Steps that
 * leave the same step side by side get a lane each; a step shares the lane of
 * its parent only when it comes after everything else in that lane, which is
 * what a merge does.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.workflowLayoutSpacing = workflowLayoutSpacing;
exports.compactCardWidths = compactCardWidths;
exports.layoutWorkflow = layoutWorkflow;
const workflow_canvas_graph_1 = require("./workflow-canvas-graph");
const workflow_card_metrics_1 = require("./workflow-card-metrics");
/** Room between the tip of an arrow and the card it points at. */
const ARROW_GAP = 6;
/** An insert place sits this far before the middle of its edge, away from the arrow. */
const SLOT_SHIFT = 4;
/** Below this length an insert place sits exactly in the middle of its edge. */
const SHORT_EDGE = 40;
/** The edge returning to a loop leaves and lands this far beside the middle of a card. */
const LOOP_BACK_OFFSET = 11;
/** Distance of the returning edge from the frame's border. */
const LOOP_BACK_INSET = 9;
const LOOP_BACK_ARROW_GAP = 5;
function metricsOf(direction, compact, laneWidth) {
    if (direction === 'LR') {
        return {
            flowGap: 64,
            laneGap: 36,
            splitExtra: 104,
            frameBefore: 26,
            frameAfter: 26,
            frameLow: 30,
            frameHigh: 22,
            splitKnee: 28,
            joinKnee: 34,
            slotAfterSource: 16,
            emptyLoopAlong: 200,
            emptyLoopAcross: 44,
            emptyLane: 44,
            loopHead: 0,
        };
    }
    return {
        flowGap: 52,
        laneGap: compact ? 10 : 40,
        splitExtra: 28,
        frameBefore: 42,
        frameAfter: 22,
        frameLow: 22,
        frameHigh: 22,
        splitKnee: 20,
        joinKnee: 26,
        slotAfterSource: 15,
        emptyLoopAlong: 44,
        emptyLoopAcross: 240,
        emptyLane: compact ? Math.min(120, laneWidth) : 120,
        loopHead: workflow_card_metrics_1.WORKFLOW_LOOP_HEAD_HEIGHT,
    };
}
/**
 * The gaps the layout keeps between steps and around a loop's body (wide, not
 * compact), for placing a step where the layout would.
 */
function workflowLayoutSpacing(direction) {
    const metrics = metricsOf(direction, false, workflow_card_metrics_1.WORKFLOW_STEP_WIDTH);
    return {
        flowGap: metrics.flowGap,
        laneGap: metrics.laneGap,
        frameBefore: metrics.frameBefore,
        frameAfter: metrics.frameAfter,
        frameLow: metrics.frameLow,
        frameHigh: metrics.frameHigh,
    };
}
/** Narrowest lane a card is squeezed into. */
const LANE_WIDTH_MIN = 120;
/**
 * Card widths for a phone: a card on the main lane still fits with a loop
 * frame around it, and two lanes fit side by side. `room` is the width the
 * workflow may use.
 */
function compactCardWidths(room) {
    const metrics = metricsOf('TB', true, LANE_WIDTH_MIN);
    const clamp = (width) => Math.max(LANE_WIDTH_MIN, Math.min(workflow_card_metrics_1.WORKFLOW_STEP_WIDTH, Math.floor(width)));
    return {
        card: clamp(room - metrics.frameLow - metrics.frameHigh),
        lane: clamp((room - metrics.laneGap) / 2),
    };
}
function layoutWorkflow(input) {
    const { graph, direction, sizes, compact, laneWidth } = input;
    const tb = direction === 'TB';
    const metrics = metricsOf(direction, compact, laneWidth);
    const topology = readTopology(graph);
    const { nodes, isLoop, isEmptyLoop, isSized } = topology;
    const count = nodes.length;
    assertNoCycle(topology);
    const rank = rankSteps(topology);
    const tree = buildTree(topology, rank);
    // 1. Which steps sit in a lane, and how much room every step takes.
    const inLane = new Uint8Array(count);
    for (const step of tree.order) {
        const parent = tree.parent[step];
        if (parent < 0) {
            inLane[step] = tree.roots.length > 1 ? 1 : 0;
            continue;
        }
        const inner = tree.inner[parent];
        const outer = tree.outer[parent];
        const row = inner.some(lane => lane.child === step)
            ? inner.length
            : outer.some(lane => lane.child === step)
                ? outer.length + (isLoop(parent) ? 1 : 0)
                : 1;
        inLane[step] = row > 1 || inLane[parent] ? 1 : 0;
    }
    const sizeOf = (step) => {
        const size = sizes.get(nodes[step].id);
        if (!size) {
            throw new Error(`Workflow layout has no size for step "${nodes[step].id}"`);
        }
        return size;
    };
    const isNarrow = (step) => tb &&
        compact &&
        inLane[step] === 1 &&
        (nodes[step].kind === 'step' || nodes[step].kind === 'branch');
    const acrossOf = (step) => {
        if (isLoop(step))
            return isEmptyLoop(step) ? metrics.emptyLoopAcross : 0;
        if (!tb)
            return sizeOf(step).h;
        return isNarrow(step) ? laneWidth : sizeOf(step).w;
    };
    const alongOf = (step) => {
        if (isLoop(step))
            return isEmptyLoop(step) ? metrics.emptyLoopAlong : 0;
        return tb ? sizeOf(step).h : sizeOf(step).w;
    };
    // 2. Across the flow, bottom-up: every subtree gets a band of its own.
    const span = Array.from({ length: count }, () => ({ lo: 0, hi: 0 }));
    const offset = new Float64Array(count);
    const frameOffset = new Float64Array(count);
    const laneOffset = new Map();
    const emptyLane = {
        lo: metrics.emptyLane / 2,
        hi: metrics.emptyLane / 2,
    };
    const laneSpan = (lane) => lane.child >= 0 ? span[lane.child] : emptyLane;
    for (let k = tree.order.length - 1; k >= 0; k--) {
        const step = tree.order[k];
        const own = acrossOf(step) / 2;
        const inner = tree.inner[step];
        const outer = tree.outer[step];
        const row = [];
        let innerRow = null;
        if (isLoop(step)) {
            innerRow = placeRow(inner.map(laneSpan), metrics.laneGap);
            const content = isEmptyLoop(step) ? { lo: own, hi: own } : innerRow.span;
            row.push({
                lo: content.lo + metrics.frameLow,
                hi: content.hi + metrics.frameHigh,
            });
        }
        for (const lane of outer)
            row.push(laneSpan(lane));
        const placed = placeRow(row, metrics.laneGap);
        const put = (lane, at) => {
            if (lane.child >= 0)
                offset[lane.child] = at;
            laneOffset.set(lane.edge.edge.id, { owner: step, offset: at });
        };
        if (innerRow) {
            frameOffset[step] = placed.centers[0];
            const centers = innerRow.centers;
            inner.forEach((lane, i) => put(lane, frameOffset[step] + centers[i]));
        }
        const first = innerRow ? 1 : 0;
        outer.forEach((lane, i) => put(lane, placed.centers[first + i]));
        let lo = Math.max(own, placed.span.lo);
        let hi = Math.max(own, placed.span.hi);
        const after = tree.after[step];
        if (after >= 0) {
            offset[after] = 0;
            lo = Math.max(lo, span[after].lo);
            hi = Math.max(hi, span[after].hi);
        }
        span[step] = { lo, hi };
    }
    const center = new Float64Array(count);
    const rootRow = placeRow(tree.roots.map(root => span[root]), metrics.laneGap);
    tree.roots.forEach((root, i) => {
        center[root] = rootRow.centers[i];
    });
    for (const step of tree.order) {
        const parent = tree.parent[step];
        if (parent >= 0)
            center[step] = center[parent] + offset[step];
    }
    /** Line the edges of a step run on. */
    const lineOf = (step) => Math.round(center[step] + (isLoop(step) ? frameOffset[step] : 0));
    // 3. Along the flow: one band per rank, with room for frames and route labels.
    const rankValues = [
        ...new Set(nodes.flatMap((_, step) => (isSized(step) ? [rank[step]] : []))),
    ].sort((a, b) => a - b);
    const bandOfRank = new Map(rankValues.map((value, i) => [value, i]));
    const band = new Int32Array(count).fill(-1);
    const bandSize = new Float64Array(rankValues.length);
    const split = new Uint8Array(rankValues.length);
    for (let step = 0; step < count; step++) {
        if (!isSized(step))
            continue;
        const at = bandOfRank.get(rank[step]);
        band[step] = at;
        bandSize[at] = Math.max(bandSize[at], alongOf(step));
        if (nodes[step].kind === 'branch' && topology.out[step].length > 0) {
            split[at] = 1;
        }
    }
    const firstBand = new Int32Array(count).fill(-1);
    const lastBand = new Int32Array(count).fill(-1);
    const loopsOf = (step) => isEmptyLoop(step)
        ? [step, ...topology.loopsAround[step]]
        : topology.loopsAround[step];
    for (let step = 0; step < count; step++) {
        if (!isSized(step))
            continue;
        for (const loop of loopsOf(step)) {
            if (firstBand[loop] < 0 || band[step] < firstBand[loop]) {
                firstBand[loop] = band[step];
            }
            if (band[step] > lastBand[loop])
                lastBand[loop] = band[step];
        }
    }
    const framesBefore = new Int32Array(rankValues.length);
    const framesAfter = new Int32Array(rankValues.length);
    for (let step = 0; step < count; step++) {
        if (!isSized(step))
            continue;
        const at = band[step];
        const enclosing = loopsOf(step);
        const starting = enclosing.filter(loop => firstBand[loop] === at).length;
        const ending = enclosing.filter(loop => lastBand[loop] === at).length;
        framesBefore[at] = Math.max(framesBefore[at], starting);
        framesAfter[at] = Math.max(framesAfter[at], ending);
    }
    const bandStart = new Float64Array(rankValues.length);
    for (let at = 0; at < rankValues.length; at++) {
        const before = framesBefore[at] * metrics.frameBefore;
        if (at === 0) {
            bandStart[at] = before;
            continue;
        }
        bandStart[at] =
            bandStart[at - 1] +
                bandSize[at - 1] +
                framesAfter[at - 1] * metrics.frameAfter +
                metrics.flowGap +
                (split[at - 1] ? metrics.splitExtra : 0) +
                before;
    }
    // 4. Boxes of the cards, then the frames from the innermost loop outwards.
    const box = nodes.map(() => null);
    for (let step = 0; step < count; step++) {
        if (!isSized(step))
            continue;
        const along = alongOf(step);
        const across = acrossOf(step);
        const m0 = Math.round(bandStart[band[step]] + (bandSize[band[step]] - along) / 2);
        const c0 = Math.round(center[step] + (isLoop(step) ? frameOffset[step] : 0) - across / 2);
        box[step] = { m0, m1: m0 + along, c0, c1: c0 + across };
    }
    const frame = nodes.map(() => null);
    const loops = nodes
        .flatMap((_, step) => (isLoop(step) ? [step] : []))
        .sort((a, b) => topology.loopsAround[b].length - topology.loopsAround[a].length || a - b);
    for (const loop of loops) {
        const inside = [];
        if (isEmptyLoop(loop))
            inside.push(box[loop]);
        for (const step of topology.body[loop]) {
            const part = isLoop(step) ? frame[step] : box[step];
            if (part)
                inside.push(part);
        }
        const around = unionOf(inside);
        if (!around)
            continue;
        frame[loop] = {
            m0: around.m0 - metrics.frameBefore,
            m1: around.m1 + metrics.frameAfter,
            c0: around.c0 - metrics.frameLow,
            c1: around.c1 + metrics.frameHigh,
        };
    }
    const boxOf = (step) => {
        const found = isLoop(step) ? frame[step] : box[step];
        if (!found) {
            throw new Error(`Workflow layout has no box for step "${nodes[step].id}"`);
        }
        return found;
    };
    // 5. Edges, insert places and route labels.
    const flowEdges = new Map();
    for (const edges of topology.out) {
        for (const edge of edges)
            flowEdges.set(edge.edge.id, edge);
    }
    const indexOf = new Map(nodes.map((node, step) => [node.id, step]));
    const paths = [];
    const slots = [];
    const labels = [];
    const outPort = (edge) => {
        const from = boxOf(edge.source);
        const c = lineOf(edge.source);
        if (!isLoop(edge.source))
            return { m: from.m1, c };
        return edge.inner
            ? { m: from.m0 + metrics.loopHead / 2, c }
            : { m: from.m1, c };
    };
    const inPort = (step) => ({
        m: boxOf(step).m0 - (isLoop(step) ? metrics.loopHead / 2 : 0),
        c: lineOf(step),
    });
    /** Middle of a stretch that ends in an arrow at `to`. */
    const slotBefore = (from, to) => to - from < SHORT_EDGE
        ? from + (to - from) / 2
        : from + (to - ARROW_GAP - from) / 2 - SLOT_SHIFT;
    const routeFlowEdge = (flow) => {
        const a = outPort(flow);
        const b = inPort(flow.target);
        const tip = { m: b.m - ARROW_GAP, c: b.c };
        const lane = laneOffset.get(flow.edge.id);
        const run = lane ? Math.round(center[lane.owner] + lane.offset) : a.c;
        const points = [a];
        let slot;
        const splits = run !== a.c;
        const joins = run !== b.c;
        const splitAt = a.m + Math.min(metrics.splitKnee, (tip.m - a.m) / 2);
        const joinAt = Math.max(b.m - metrics.joinKnee, splits ? splitAt : a.m);
        if (splits)
            points.push({ m: splitAt, c: a.c }, { m: splitAt, c: run });
        if (joins)
            points.push({ m: joinAt, c: run }, { m: joinAt, c: b.c });
        points.push(tip);
        if (splits) {
            slot = joins
                ? { m: (splitAt + joinAt) / 2, c: run }
                : { m: slotBefore(splitAt, b.m), c: run };
        }
        else if (joins) {
            slot = { m: a.m + metrics.slotAfterSource, c: a.c };
        }
        else {
            slot = { m: slotBefore(a.m, b.m), c: a.c };
        }
        paths.push({ id: flow.edge.id, kind: 'flow', points });
        slots.push({
            edge: flow.edge,
            place: (0, workflow_canvas_graph_1.insertPlaceOnEdge)(nodes[flow.source], flow.edge),
            point: slot,
        });
        const source = nodes[flow.source];
        if (source.kind === 'branch' &&
            source.routes.some(route => route.id === flow.edge.routeId)) {
            labels.push({ edge: flow.edge, point: slot });
        }
    };
    const routeLoopBack = (edge) => {
        const loop = indexOf.get(edge.target);
        const source = indexOf.get(edge.source);
        const around = boxOf(loop);
        const entry = topology.out[loop].find(out => out.inner && out.target !== loop);
        const landing = entry ? boxOf(entry.target) : (box[loop] ?? around);
        const from = source === loop ? landing : boxOf(source);
        const leave = (from.m0 + from.m1) / 2 + LOOP_BACK_OFFSET;
        const land = (landing.m0 + landing.m1) / 2 - LOOP_BACK_OFFSET;
        const rail = around.c1 - LOOP_BACK_INSET;
        paths.push({
            id: edge.id,
            kind: 'loop-back',
            points: [
                { m: leave, c: from.c1 },
                { m: leave, c: rail },
                { m: land, c: rail },
                { m: land, c: landing.c1 + LOOP_BACK_ARROW_GAP },
            ],
        });
        slots.push({
            edge,
            place: (0, workflow_canvas_graph_1.insertPlaceOnEdge)(nodes[source], edge),
            // A loop without steps offers its insert place in the middle of the frame.
            point: isEmptyLoop(loop) && source === loop
                ? {
                    m: (landing.m0 + landing.m1) / 2,
                    c: (landing.c0 + landing.c1) / 2,
                }
                : { m: (leave + land) / 2, c: rail },
        });
    };
    for (const edge of graph.edges) {
        const flow = flowEdges.get(edge.id);
        if (flow)
            routeFlowEdge(flow);
        else
            routeLoopBack(edge);
    }
    // 6. Into world coordinates, with the top left corner of everything at 0,0.
    const worldPoint = (point) => tb
        ? { x: Math.round(point.c), y: Math.round(point.m) }
        : { x: Math.round(point.m), y: Math.round(point.c) };
    const worldRect = (flowBox) => tb
        ? {
            x: flowBox.c0,
            y: flowBox.m0,
            w: flowBox.c1 - flowBox.c0,
            h: flowBox.m1 - flowBox.m0,
        }
        : {
            x: flowBox.m0,
            y: flowBox.c0,
            w: flowBox.m1 - flowBox.m0,
            h: flowBox.c1 - flowBox.c0,
        };
    const rects = new Map();
    for (let step = 0; step < count; step++) {
        const found = isLoop(step) ? frame[step] : box[step];
        if (found)
            rects.set(nodes[step].id, worldRect(found));
    }
    const edgePoints = paths.map(path => path.points.map(worldPoint));
    const slotPoints = slots.map(slot => worldPoint(slot.point));
    const labelPoints = labels.map(label => worldPoint(label.point));
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const cover = (x, y) => {
        x0 = Math.min(x0, x);
        y0 = Math.min(y0, y);
        x1 = Math.max(x1, x);
        y1 = Math.max(y1, y);
    };
    for (const rect of rects.values()) {
        cover(rect.x, rect.y);
        cover(rect.x + rect.w, rect.y + rect.h);
    }
    for (const point of [...edgePoints.flat(), ...slotPoints]) {
        cover(point.x, point.y);
    }
    if (x0 === Infinity) {
        return {
            nodes: rects,
            narrow: new Set(),
            edges: [],
            frames: [],
            routeLabels: [],
            slots: [],
            bounds: { x: 0, y: 0, w: 0, h: 0 },
        };
    }
    const moved = (point) => ({
        x: point.x - x0,
        y: point.y - y0,
    });
    for (const rect of rects.values()) {
        rect.x -= x0;
        rect.y -= y0;
    }
    return {
        nodes: rects,
        narrow: new Set(tree.order.filter(isNarrow).map(step => nodes[step].id)),
        edges: paths.map((path, i) => ({
            id: path.id,
            points: edgePoints[i].map(moved),
            kind: path.kind,
            state: 'idle',
        })),
        frames: loops.flatMap(loop => {
            const rect = rects.get(nodes[loop].id);
            return rect ? [{ loopId: nodes[loop].id, rect }] : [];
        }),
        routeLabels: labels.map((label, i) => ({
            branchId: label.edge.source,
            routeId: label.edge.routeId,
            edgeId: label.edge.id,
            point: moved(labelPoints[i]),
        })),
        slots: slots.map((slot, i) => ({
            edgeId: slot.edge.id,
            place: slot.place,
            point: moved(slotPoints[i]),
        })),
        bounds: { x: 0, y: 0, w: x1 - x0, h: y1 - y0 },
    };
}
function readTopology(graph) {
    const nodes = graph.nodes;
    const indexOf = new Map(nodes.map((node, step) => [node.id, step]));
    const stepOf = (id, edge) => {
        const step = indexOf.get(id);
        if (step == null) {
            throw new Error(`Workflow edge "${edge.id}" references unknown step "${id}"`);
        }
        return step;
    };
    const out = nodes.map(() => []);
    const incoming = nodes.map(() => []);
    const routeOrder = new Map();
    for (const edge of graph.edges) {
        const source = stepOf(edge.source, edge);
        const target = stepOf(edge.target, edge);
        if (edge.kind !== 'flow')
            continue;
        const from = nodes[source];
        const inner = from.kind === 'loop' && edge.routeId === workflow_canvas_graph_1.LOOP_CONTINUE_ROUTE;
        const flow = {
            edge,
            source,
            target,
            inner,
            outputIndex: (0, workflow_canvas_graph_1.outputIndexOf)(from, edge.routeId),
        };
        const route = from.routes.findIndex(item => item.id === edge.routeId);
        routeOrder.set(flow, from.kind === 'branch' && route < 0
            ? from.routes.length
            : flow.outputIndex);
        out[source].push(flow);
        incoming[target].push(flow);
    }
    // Lanes follow the order of the outputs; edges of one output keep their order.
    for (const edges of out) {
        const stored = new Map(edges.map((edge, i) => [edge, i]));
        edges.sort((a, b) => routeOrder.get(a) - routeOrder.get(b) ||
            stored.get(a) - stored.get(b));
    }
    const loopsAround = nodes.map(node => {
        const loops = [];
        for (let loop = node.loopId == null ? undefined : indexOf.get(node.loopId); loop != null && !loops.includes(loop); loop =
            nodes[loop].loopId == null
                ? undefined
                : indexOf.get(nodes[loop].loopId)) {
            loops.push(loop);
        }
        return loops;
    });
    const body = nodes.map(() => []);
    loopsAround.forEach((loops, step) => {
        for (const loop of loops)
            body[loop].push(step);
    });
    const isLoop = (step) => nodes[step].kind === 'loop';
    const isEmptyLoop = (step) => isLoop(step) && body[step].length === 0;
    return {
        nodes,
        out,
        in: incoming,
        loopsAround,
        body,
        isLoop,
        isEmptyLoop,
        isSized: step => !isLoop(step) || isEmptyLoop(step),
    };
}
/** A workflow may only run in a circle through a loop step. */
function assertNoCycle(topology) {
    const pending = topology.in.map(edges => edges.length);
    const queue = pending.flatMap((open, step) => (open === 0 ? [step] : []));
    // The queue grows while it is walked.
    for (const at of queue) {
        for (const edge of topology.out[at]) {
            if (--pending[edge.target] === 0)
                queue.push(edge.target);
        }
    }
    if (queue.length === topology.nodes.length)
        return;
    // Every step left over has a predecessor that is left over too.
    const seenAt = new Map();
    const walked = [];
    let at = pending.findIndex(open => open > 0);
    while (!seenAt.has(at)) {
        seenAt.set(at, walked.length);
        walked.push(at);
        at = topology.in[at].find(edge => pending[edge.source] > 0)
            .source;
    }
    const circle = walked.slice(seenAt.get(at));
    const forward = [circle[0], ...circle.slice(1).reverse(), circle[0]];
    throw new Error(`Workflow has a cycle outside a loop: ${forward
        .map(step => topology.nodes[step].id)
        .join(' → ')}`);
}
/**
 * Rank of every step along the flow: the longest way that leads to it. A loop
 * shares the rank of its first step, and what follows a loop comes after
 * everything inside it.
 */
function rankSteps(topology) {
    const count = topology.nodes.length;
    const loops = topology.nodes.flatMap((_, step) => topology.isLoop(step) ? [step] : []);
    const next = Array.from({ length: count + loops.length }, () => []);
    for (const edges of topology.out) {
        for (const edge of edges) {
            next[edge.source].push({ to: edge.target, weight: edge.inner ? 0 : 1 });
        }
    }
    loops.forEach((loop, i) => {
        const end = count + i;
        const exits = topology.out[loop]
            .filter(edge => !edge.inner)
            .map(edge => edge.target);
        // A step the exit leads back to cannot also come before the exit.
        const behind = new Uint8Array(next.length);
        const stack = [...exits];
        for (let at = stack.pop(); at != null; at = stack.pop()) {
            if (behind[at])
                continue;
            behind[at] = 1;
            for (const step of next[at])
                stack.push(step.to);
        }
        next[loop].push({ to: end, weight: 1 });
        for (const step of topology.body[loop]) {
            if (!behind[step])
                next[step].push({ to: end, weight: 1 });
        }
        for (const exit of exits)
            next[end].push({ to: exit, weight: 0 });
    });
    const pending = new Int32Array(next.length);
    for (const steps of next)
        for (const step of steps)
            pending[step.to]++;
    const rank = next.map(() => 0);
    const queue = [];
    pending.forEach((open, step) => {
        if (open === 0)
            queue.push(step);
    });
    // The queue grows while it is walked.
    for (const at of queue) {
        for (const step of next[at]) {
            rank[step.to] = Math.max(rank[step.to], rank[at] + step.weight);
            if (--pending[step.to] === 0)
                queue.push(step.to);
        }
    }
    return rank.slice(0, count);
}
function buildTree(topology, rank) {
    const count = topology.nodes.length;
    const parent = new Int32Array(count).fill(-1);
    const via = topology.nodes.map(() => null);
    const children = topology.nodes.map(() => []);
    // Every step hangs below the predecessor that is furthest along the flow.
    for (let step = 0; step < count; step++) {
        let best = null;
        for (const edge of topology.in[step]) {
            if (!best || rank[edge.source] > rank[best.source])
                best = edge;
        }
        if (!best)
            continue;
        parent[step] = best.source;
        via[step] = best;
        children[best.source].push(step);
    }
    const pathUp = (step) => {
        const path = [];
        for (let at = step; at >= 0; at = parent[at])
            path.push(at);
        return path;
    };
    /** Whether `step` comes after everything else below `owner`. */
    const closes = (owner, step) => {
        const own = new Set();
        const below = [step];
        for (let at = below.pop(); at != null; at = below.pop()) {
            own.add(at);
            below.push(...children[at]);
        }
        const stack = [...children[owner]];
        for (let at = stack.pop(); at != null; at = stack.pop()) {
            if (own.has(at))
                continue;
            if (rank[at] >= rank[step])
                return false;
            stack.push(...children[at]);
        }
        return true;
    };
    // A step where lanes meet moves up to the step the lanes started from, so
    // it sits on that step's line. Later steps first: their place is final then.
    const joins = topology.nodes
        .flatMap((_, step) => new Set(topology.in[step].map(edge => edge.source)).size > 1 ? [step] : [])
        .sort((a, b) => rank[b] - rank[a] || a - b);
    for (const step of joins) {
        let common = pathUp(topology.in[step][0].source);
        for (const edge of topology.in[step].slice(1)) {
            const other = new Set(pathUp(edge.source));
            common = common.filter(at => other.has(at));
        }
        const owner = common[0];
        if (owner == null || owner === parent[step] || !closes(owner, step)) {
            continue;
        }
        const siblings = children[parent[step]];
        siblings.splice(siblings.indexOf(step), 1);
        parent[step] = owner;
        via[step] = null;
        children[owner].push(step);
    }
    const roots = topology.nodes.flatMap((_, step) => parent[step] < 0 ? [step] : []);
    const order = [];
    const stack = [...roots].reverse();
    for (let at = stack.pop(); at != null; at = stack.pop()) {
        order.push(at);
        for (let i = children[at].length - 1; i >= 0; i--) {
            stack.push(children[at][i]);
        }
    }
    const deepest = new Int32Array(count);
    const after = new Int32Array(count).fill(-1);
    const inner = topology.nodes.map(() => []);
    const outer = topology.nodes.map(() => []);
    for (let k = order.length - 1; k >= 0; k--) {
        const step = order[k];
        deepest[step] = rank[step];
        for (const child of children[step]) {
            deepest[step] = Math.max(deepest[step], deepest[child]);
        }
        // The steps a loop repeats stay in its frame and never follow it.
        const isLoop = topology.isLoop(step);
        let last = -1;
        for (const child of children[step]) {
            if (isLoop && via[child]?.inner)
                continue;
            if (last < 0 || rank[child] > rank[last])
                last = child;
        }
        if (last >= 0 &&
            children[step].every(child => child === last || deepest[child] < rank[last])) {
            after[step] = last;
        }
        const groups = isLoop
            ? [
                topology.out[step].filter(edge => edge.inner),
                topology.out[step].filter(edge => !edge.inner),
            ]
            : [[], topology.out[step]];
        groups.forEach((group, i) => {
            for (const edge of group) {
                const startsLane = parent[edge.target] === step &&
                    via[edge.target] === edge &&
                    after[step] !== edge.target;
                if (!startsLane && group.length < 2)
                    continue;
                (i === 0 ? inner : outer)[step].push({
                    edge,
                    child: startsLane ? edge.target : -1,
                });
            }
        });
    }
    return { parent, children, roots, after, inner, outer, order };
}
/** Puts lanes side by side around a centre line; a single lane sits on it. */
function placeRow(lanes, gap) {
    if (lanes.length === 0)
        return { centers: [], span: { lo: 0, hi: 0 } };
    if (lanes.length === 1)
        return { centers: [0], span: lanes[0] };
    const total = lanes.reduce((sum, lane) => sum + lane.lo + lane.hi, 0) +
        gap * (lanes.length - 1);
    let cursor = -total / 2;
    const centers = lanes.map(lane => {
        const at = cursor + lane.lo;
        cursor += lane.lo + lane.hi + gap;
        return at;
    });
    return { centers, span: { lo: total / 2, hi: total / 2 } };
}
function unionOf(boxes) {
    if (boxes.length === 0)
        return null;
    return boxes.reduce((all, part) => ({
        m0: Math.min(all.m0, part.m0),
        m1: Math.max(all.m1, part.m1),
        c0: Math.min(all.c0, part.c0),
        c1: Math.max(all.c1, part.c1),
    }));
}
